import {
  AdditiveBlending,
  BackSide,
  Color,
  Mesh,
  MeshBasicMaterial,
  type MeshStandardMaterial,
  type Object3D,
} from 'three';
import type { PointerInteraction } from '../interaction/pointer';
import { devBus } from '../debug/devBus';

// Visual feedback for hovered + selected hero objects.
//
// Two tiers stacked on top of the existing boombox emissive trick:
//
//   - HOVER  — boost emissiveIntensity on every MeshStandardMaterial in the
//              hovered subtree. Restore on hoverOut. Near-zero per-frame
//              cost: one material walk per hover transition.
//   - SELECT — a stronger emissive tint on the ACTUAL geo (no duplicate). An
//              inverted-hull outline (a scaled back-face clone) is available via
//              `deps.outline` but is OFF by default: the hull reads as a bright
//              clone sitting over the mesh, which hides the geo while authoring —
//              so authoring keeps it off and relies on the emissive tint alone.
//
// State model:
//   - Click a hero        → selects it (replaces any prior selection)
//   - Click the same hero → deselects (toggle)
//   - Click empty space   → deselects
//
// Per-hero opt-outs via userData on the hero root:
//   - `no_outline:    true` — skip the inverted-hull pass (use for open
//                              surfaces like cloth where the hull doesn't
//                              work cleanly). Emissive boost still applied.
//   - `no_highlight:  true` — skip the entire highlight system. Useful for
//                              decorative meshes that should react via
//                              their own custom shader.
//
// Material-of-record:
//   We mutate `emissive` + `emissiveIntensity` on each MeshStandardMaterial
//   we touch, AFTER snapshotting the originals so dispose() can restore. If
//   a material is shared across multiple heroes, this is broken (snapshot
//   gets overwritten). In practice GLTFLoader clones materials per primitive
//   in this project so it's safe; if a future material gets explicitly
//   shared, opt that hero out via `no_highlight`.

export interface HoverHighlightDeps {
  pointer: PointerInteraction;
  /** Hover emissive intensity (added to original). 0.35 reads as "subtle pop". */
  hoverEmissive?: number;
  /** Selection emissive intensity (added to original). 0.7 reads as "this is
   *  actively chosen — and stays so after pointer leaves". */
  selectEmissive?: number;
  /** Highlight color. Warm cream by default so it reads against the room's
   *  warm/amber palette without screaming neon. */
  color?: Color;
  /** When false, a click does NOT promote to a persistent selection (no
   *  inverted-hull outline, no sustained emissive) — hover feedback is
   *  untouched. Demo view sets this false: clicking a hero there flashes a
   *  transient chip (createDemoChip) instead of leaving a stuck highlight,
   *  which read as a UI artifact in the diegetic scene. The authoring views
   *  keep selection (the inspector needs a durable "active hero"). Default true. */
  select?: boolean;
  /** When false (default), selection draws NO inverted-hull outline — just the
   *  emissive tint on the actual geo. The hull is a scaled clone that reads as a
   *  bright duplicate over the mesh (hides it while authoring), so it's off
   *  unless a caller explicitly opts in. */
  outline?: boolean;
  /** DM7 — "selected = editable." When set, a 3D click on a hero routes the
   *  selection through this callback (app.ts wires it to setEditTarget) so the
   *  gizmo attaches on the click — no second trip to an inspector row. The
   *  callback owns the hero:select broadcast (setEditTarget emits it); when
   *  absent, handleClick emits hero:select itself (the pre-DM7 behavior). */
  onPick?: (heroId: string | null) => void;
}

interface MaterialSnapshot {
  mat: MeshStandardMaterial;
  emissive: Color;
  intensity: number;
}

export class HoverHighlight {
  private hoverEmissive: number;
  private selectEmissive: number;
  private color: Color;
  private select: boolean;
  private outline: boolean;
  private onPick?: (heroId: string | null) => void;

  private hoveredRoot: Object3D | null = null;
  private selectedRoot: Object3D | null = null;
  private focusedRoot: Object3D | null = null;
  private hoverSnapshots: MaterialSnapshot[] = [];
  private selectSnapshots: MaterialSnapshot[] = [];
  private focusSnapshots: MaterialSnapshot[] = [];
  private outlineMesh: Object3D | null = null;
  private outlineParent: Object3D | null = null;
  private disposeHandlers: Array<() => void> = [];

  constructor(deps: HoverHighlightDeps) {
    this.hoverEmissive = deps.hoverEmissive ?? 0.2;   // relaxed (was 0.35) — subtler hover, less glare
    this.selectEmissive = deps.selectEmissive ?? 0.5; // "slightly colored" — subtler now the hull is gone
    this.color = (deps.color ?? new Color(0xe8b478)).clone(); // warm amber (was cream 0xfff2c8) — reads less white
    this.select = deps.select ?? true;
    this.outline = deps.outline ?? false; // hull OFF by default — just the emissive tint on the real geo
    this.onPick = deps.onPick;

    this.disposeHandlers.push(
      deps.pointer.on('hoverIn', (info) => this.handleHoverIn(info.object)),
    );
    this.disposeHandlers.push(
      deps.pointer.on('hoverOut', () => this.handleHoverOut()),
    );
    this.disposeHandlers.push(
      deps.pointer.on('click', (info) => this.handleClick(info.object)),
    );
    // Click on empty space (no hero hit) → deselect. Mirrors a toggle-off:
    // clears the emissive tint AND detaches the gizmo (broadcastSelect → onPick →
    // setEditTarget('(none)')). The pointer fires this only on a genuine tap that
    // hit nothing — a drag/orbit is filtered by its slop check — so orbiting the
    // empty room never deselects.
    this.disposeHandlers.push(
      deps.pointer.onEmptyClick(() => this.handleEmptyClick()),
    );
  }

  /** Programmatic selection from a UI panel (inspector row / edit target).
   *  Mirrors a 3D click's visuals but does NOT emit hero:select — the caller
   *  already owns the selection signal, so this can't feed back into a loop. */
  selectRoot(root: Object3D | null): void {
    if (root && root !== this.selectedRoot) this.handleHoverOut();
    this.setSelectedRoot(root);
  }

  /** Cross-surface "focus" highlight (hover-tier emissive), driven by the
   *  audio:focus bus and routed in from app.ts: hovering a timeline SOUND
   *  chip / BED bar / CUE marker or an inspector palette chip lights the
   *  anchored hero here in 3D. Emit-free (the bus already owns the signal, so
   *  this can't loop) and a separate snapshot stack from hover/selection so
   *  it composes cleanly. Skips re-boosting a root that hover or selection
   *  already lit, so the emissive never double-stacks. */
  focusRoot(root: Object3D | null): void {
    if (root === this.focusedRoot) return;
    this.restoreSnapshots(this.focusSnapshots);
    this.focusSnapshots = [];
    this.focusedRoot = root;
    if (root && root !== this.hoveredRoot && root !== this.selectedRoot && !this.isOptedOut(root)) {
      this.collectAndBoost(root, this.hoverEmissive, this.focusSnapshots);
    }
  }

  /** Programmatically clear selection — e.g. when a UI panel changes state
   *  and the previously-selected hero shouldn't read as "still selected".
   *  Hover state is independent and stays put. */
  clearSelection(): void {
    if (this.selectedRoot) {
      this.setSelectedRoot(null);
      devBus.emit('hero:select', { id: null });
    }
  }

  /** Apply (or clear) the selection visuals — emissive boost + inverted-hull
   *  outline — without emitting. The single visual primitive shared by the
   *  click handler, the programmatic selectRoot, and clearSelection. */
  private setSelectedRoot(root: Object3D | null): void {
    if (root === this.selectedRoot) return;
    // If this root is currently focus-lit, drop the focus boost first so the
    // selection snapshot captures the true original emissive (not the
    // focus-boosted value) and the two tiers can't mis-order on restore.
    if (root && root === this.focusedRoot) this.focusRoot(null);
    this.restoreSnapshots(this.selectSnapshots);
    this.selectSnapshots = [];
    this.removeOutline();
    this.selectedRoot = root;
    if (root && !this.isOptedOut(root)) {
      this.collectAndBoost(root, this.selectEmissive, this.selectSnapshots);
      if (this.outline && !this.skipOutline(root)) this.addOutline(root);
    }
  }

  dispose(): void {
    this.clearSelection();
    this.focusRoot(null);
    this.handleHoverOut();
    for (const fn of this.disposeHandlers) fn();
    this.disposeHandlers = [];
  }

  // ── private ────────────────────────────────────────────────────────────

  private isOptedOut(root: Object3D): boolean {
    return root.userData?.no_highlight === true;
  }

  private skipOutline(root: Object3D): boolean {
    return root.userData?.no_outline === true;
  }

  /** Walk the root's subtree, collect MeshStandardMaterials whose emissive
   *  we can safely boost. Snapshots are pushed onto the provided array so
   *  restore() can undo exactly what we did. */
  private collectAndBoost(root: Object3D, intensity: number, snapshots: MaterialSnapshot[]): void {
    root.traverse((child) => {
      const mesh = child as Mesh;
      if (!mesh.isMesh) return;
      const mat = mesh.material as MeshStandardMaterial | MeshStandardMaterial[];
      const materials = Array.isArray(mat) ? mat : [mat];
      for (const m of materials) {
        // Duck-check rather than instanceof — bundlers sometimes split three
        // across versions and break instanceof, but the .emissive field is
        // stable since r80.
        if (!m || !(m as MeshStandardMaterial).emissive) continue;
        const std = m as MeshStandardMaterial;
        snapshots.push({
          mat: std,
          emissive: std.emissive.clone(),
          intensity: std.emissiveIntensity,
        });
        // Add (not replace) so a material that's already emissive (the
        // boombox in "playing" state) doesn't visually reset on hover.
        std.emissive.copy(std.emissive).add(this.color.clone().multiplyScalar(intensity));
        std.emissiveIntensity = std.emissiveIntensity + intensity;
      }
    });
  }

  private restoreSnapshots(snapshots: MaterialSnapshot[]): void {
    for (const s of snapshots) {
      s.mat.emissive.copy(s.emissive);
      s.mat.emissiveIntensity = s.intensity;
    }
  }

  private handleHoverIn(obj: Object3D): void {
    const root = this.resolveRoot(obj);
    if (!root || this.isOptedOut(root)) return;
    // Don't re-apply hover boost if this root is already lit by selection or
    // cross-surface focus — they already show it, and double-applying would
    // shift the emissive out of bounds.
    if (root === this.selectedRoot || root === this.focusedRoot) {
      this.hoveredRoot = root;
      return;
    }
    if (this.hoveredRoot === root) return;
    this.handleHoverOut();
    this.hoveredRoot = root;
    this.collectAndBoost(root, this.hoverEmissive, this.hoverSnapshots);
    devBus.emit('hero:hover', { id: heroIdOf(root) });
  }

  private handleHoverOut(): void {
    if (!this.hoveredRoot) return;
    this.restoreSnapshots(this.hoverSnapshots);
    this.hoverSnapshots = [];
    this.hoveredRoot = null;
    devBus.emit('hero:hover', { id: null });
  }

  private handleClick(obj: Object3D): void {
    // Demo: selection is disabled — a click leaves no persistent highlight (the
    // hover boost stays while the pointer rests on the hero, and clears on
    // hoverOut). The transient click chip is owned by createDemoChip, fed by the
    // same pointer 'click', so nothing is lost here.
    if (!this.select) return;
    const root = this.resolveRoot(obj);
    if (!root) {
      // Empty-space click would route to a different code path; the pointer
      // system only fires `click` on interactive hits.
      return;
    }
    if (this.isOptedOut(root)) return;
    // Toggle off if clicking the current selection.
    if (root === this.selectedRoot) {
      this.setSelectedRoot(null);
      this.broadcastSelect(null);
      // Hover-in-place after deselect — restore hover boost (if user is
      // still over the same hero).
      this.handleHoverIn(obj);
      return;
    }
    // Promote: drop the hover boost (it would double-stack), then select.
    this.handleHoverOut();
    this.setSelectedRoot(root);
    this.broadcastSelect(heroIdOf(root));
  }

  /** Click on empty space → deselect (mirror of the same-hero toggle-off, minus
   *  the re-hover since there's no hero under the cursor). No-op if nothing is
   *  selected, or in demo where selection is disabled. */
  private handleEmptyClick(): void {
    if (!this.select || !this.selectedRoot) return;
    this.setSelectedRoot(null);
    this.broadcastSelect(null);
  }

  /** DM7 — route a 3D-click selection through onPick (→ setEditTarget, which
   *  attaches the gizmo AND emits hero:select) when wired; otherwise emit
   *  hero:select directly. Either way exactly one hero:select reaches the bus,
   *  so the AABB + outline fan-out is unchanged. */
  private broadcastSelect(id: string | null): void {
    if (this.onPick) this.onPick(id);
    else devBus.emit('hero:select', { id });
  }

  /** Walk up to the topmost ancestor still carrying `hero_id` userData. The
   *  pointer system already gives us the hero root, but a future click that
   *  bypasses the engine might pass an inner mesh; resolving here is cheap
   *  insurance. */
  private resolveRoot(obj: Object3D): Object3D | null {
    let cur: Object3D | null = obj;
    while (cur) {
      if (cur.userData?.hero_id) return cur;
      cur = cur.parent;
    }
    return obj; // fall back to whatever the pointer handed us
  }

  /** Inverted-hull outline: clone meshes in the subtree, render back-faces
   *  only at a slight outset, additive blend, depthWrite off. Looks like a
   *  thin colored rim around the silhouette without any post-processing. */
  private addOutline(root: Object3D): void {
    const outline = root.clone(true);
    // Strip userData so the clone can't be raycast / interacted with — it's
    // pure decoration. Also strip children that lack geometry.
    outline.traverse((child) => {
      const mesh = child as Mesh;
      if (!mesh.isMesh) {
        child.userData = {};
        return;
      }
      const outlineMat = new MeshBasicMaterial({
        color: this.color,
        side: BackSide,
        transparent: true,
        opacity: 0.85,
        depthWrite: false,
        blending: AdditiveBlending,
      });
      mesh.material = outlineMat;
      mesh.renderOrder = 999;
      mesh.userData = {};
      mesh.scale.multiplyScalar(1.04);
    });
    // Match the original's world transform exactly. addOutline runs after
    // selection has been committed, so root's matrix is already current.
    outline.position.copy(root.position);
    outline.quaternion.copy(root.quaternion);
    outline.scale.copy(root.scale).multiplyScalar(1.04);
    // Outline is parented to the root's parent so it follows world drags
    // without needing manual sync.
    const parent = root.parent ?? root;
    parent.add(outline);
    this.outlineMesh = outline;
    this.outlineParent = parent;
  }

  private removeOutline(): void {
    if (!this.outlineMesh) return;
    if (this.outlineParent) this.outlineParent.remove(this.outlineMesh);
    this.outlineMesh.traverse((c) => {
      const mesh = c as Mesh;
      if (mesh.isMesh) {
        const m = mesh.material as MeshBasicMaterial | MeshBasicMaterial[];
        if (Array.isArray(m)) m.forEach((x) => x.dispose());
        else m.dispose();
      }
    });
    this.outlineMesh = null;
    this.outlineParent = null;
  }
}

/** Pull the hero_id string from a resolved root, or null if missing. The
 *  outer function is module-scoped (not a class method) so devBus payloads
 *  inside the class can be built without leaking `this` references into a
 *  free function. */
function heroIdOf(root: Object3D): string | null {
  const id = root.userData?.hero_id;
  return typeof id === 'string' ? id : null;
}
